// src/pages/auth/Login.tsx
import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import Button from '../../components/common/Button';
import { RootState } from '../../store';
import { login, clearError } from '../../store/slices/authSlice';
import { fetchRoles, fetchPermissions } from '../../store/slices/permissionsSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';
import Icon from '../../components/icons/Icons';

const PageContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  background-color: ${({ theme }) => theme.colours.background.default};
`;

const LoginCard = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  width: 100%;
  max-width: 450px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  padding: 2rem;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  font-weight: 600;
  text-align: center;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colours.text.secondary};
  text-align: center;
  margin-bottom: 2rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1.5rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  margin-bottom: 0.5rem;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  border: 1px solid #DDDDDD;
  border-radius: 0.25rem;
  font-size: 0.875rem;
  font-family: ${({ theme }) => theme.typography.fontFamily};
  
  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.colours.primary};
    box-shadow: 0 0 0 1px ${({ theme }) => theme.colours.primary};
  }
`;

const RememberMeContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
`;

const RememberMeLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colours.text.primary};
  cursor: pointer;
  user-select: none;
`;

const ToggleLabel = styled.a`
  font-size: 0.875rem;
  color: ${({ theme }) => theme.colours.text.primary};
  cursor: pointer;
  font-weight: 500;
`;

const LoginButton = styled(Button)`
  width: 100%;
  background-color: #1A0633;
  color: white;
  
  &:hover {
    background-color: #12042A;
  }
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  font-size: 0.875rem;
  margin-bottom: 1rem;
  text-align: center;
  padding: 0.5rem;
  background-color: #ffebee;
  border-radius: 0.25rem;
  display: ${props => props.children ? 'block' : 'none'};
`;

interface LoginFormData {
  email: string;
  password: string;
  rememberMe: boolean;
}

const Login: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const togglePasswordVisibility = () => setShowPassword((prev) => !prev);
  const [searchParams] = useSearchParams();
  const { isLoading, error, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );

  const [formData, setFormData] = useState<LoginFormData>({
    email: '',
    password: '',
    rememberMe: false
  });

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      // Fetch roles and permissions after successful authentication
      dispatch(fetchRoles());
      dispatch(fetchPermissions());
      navigate('/dashboard');
    }
  }, [isAuthenticated, navigate, dispatch]);

  // Clear any errors on component mount
  useEffect(() => {
    dispatch(clearError());
  }, [dispatch]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.password) return;
    // Dispatch login action with form data
    const result = await dispatch(login({
      email: formData.email,
      password: formData.password
    }));

    // Only navigate on successful login
    if (login.fulfilled.match(result)) {
      const redirectUrl = searchParams.get('redirect');
      if (redirectUrl) {
        navigate(decodeURIComponent(redirectUrl), { replace: true });
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  };

  return (
    <PageContainer>
      <LoginCard>
        <Title>Authentication</Title>
        <Subtitle>Fill in your email address and password to have access to the system.</Subtitle>
        
        {error && <ErrorMessage>{error}</ErrorMessage>}
        
        <form onSubmit={handleLogin}>
          <FormGroup>
            <Label htmlFor="email">Email Address</Label>
            <Input
              type="email"
              id="email"
              name="email"
              placeholder="Enter Email Address"
              value={formData.email}
              onChange={handleChange}
              required
            />
          </FormGroup>
          
          <FormGroup style={{ position: "relative" }}>
            <Label htmlFor="password">Password</Label>
            <Input
              type={showPassword ? "text" : "password"}
              id="password"
              name="password"
              placeholder="Enter Password"
              value={formData.password}
              onChange={handleChange}
              required
            />
            <span
              onClick={togglePasswordVisibility}
              style={{
                position: "absolute",
                right: "10px",
                top: "50%",
                transform: "translateY(-50%)",
                cursor: "pointer",
              }}
            >
              {showPassword ? <Icon name='closedEye'/> : <Icon name='openEye'/>}
            </span>
          </FormGroup>

          
          <RememberMeContainer>
            <RememberMeLabel htmlFor="rememberMe">
              <input
                type="checkbox"
                id="rememberMe"
                name="rememberMe"
                checked={formData.rememberMe}
                onChange={handleChange}
              />
              Remember me
            </RememberMeLabel>
            <ToggleLabel href={'/forgot-password'}>Forgot Password</ToggleLabel>
          </RememberMeContainer>
          
          <LoginButton 
            type="submit" 
            size="large" 
            disabled={isLoading}
          >
            {isLoading ? 'Logging in...' : 'Login'}
          </LoginButton>
        </form>
      </LoginCard>
    </PageContainer>
  );
};

export default Login;